/**
 * Figures the revamped Today and Adherence History screens show, derived from
 * the two payloads the app already fetches: /device/:id/dashboard and
 * /device/:id/adherence.
 *
 * Nothing here invents data. Anything the endpoints cannot support is simply
 * not produced.
 */
import { DateTime } from "luxon";

/** Compartments at or below this remaining quantity are flagged as low. */
export const LOW_STOCK_THRESHOLD = 5;

/** The device stores a fixed 5-slot alarm array; 0 marks an unused slot. */
export const MAX_ALARMS = 5;

/** Pad an alarm list out to the device's fixed slot count. */
export const padAlarms = (alarms = []) => Array.from({ length: MAX_ALARMS }, (_, i) => alarms[i] ?? 0);

/** "08:00" from an alarm code (minutes since midnight). 24-hour, to match
 * the rest of the app (Notifications, Adherence History). */
export const alarmLabel = (code) => {
  const minutes = Math.abs(code) % (24 * 60);
  const h24 = Math.floor(minutes / 60);
  return `${String(h24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
};

export const parseDay = (dateString, zone) => {
  const [day, month, year] = String(dateString).split("-").map(Number);
  return DateTime.fromObject({ day, month, year }, { zone });
};

export const clockLabel = (iso, zone) => DateTime.fromISO(iso).setZone(zone).toFormat("HH:mm");

const minutesNow = (zone) => {
  const now = DateTime.now().setZone(zone);
  return now.hour * 60 + now.minute;
};

/** Adherence across the last `size` days, optionally offset further back. */
export const adherenceOver = (days = [], size, offset = 0) => {
  const end = days.length - offset;
  const window = days.slice(Math.max(0, end - size), end);
  const taken = window.reduce((sum, d) => sum + (d.metrics?.pillsTaken || 0), 0);
  const missed = window.reduce((sum, d) => sum + (d.metrics?.pillsMissed || 0), 0);
  const total = taken + missed;
  return { taken, missed, total, pct: total ? Math.round((taken / total) * 100) : null };
};

/** Percentage-point change of the last 7 days against the 7 before them. */
export const weekOverWeek = (days = []) => {
  if (days.length < 14) return null;
  const now = adherenceOver(days, 7);
  const previous = adherenceOver(days, 7, 7);
  if (now.pct === null || previous.pct === null) return null;
  return now.pct - previous.pct;
};

/** Per-day adherence for the trend chart. */
export const dailySeries = (days = [], size, zone) =>
  days.slice(-size).map((day) => {
    const taken = day.metrics?.pillsTaken || 0;
    const missed = day.metrics?.pillsMissed || 0;
    const total = taken + missed;
    return {
      date: day.date,
      label: parseDay(day.date, zone).toFormat("ccc"),
      pct: total ? Math.round((taken / total) * 100) : null,
      missed,
    };
  });

/**
 * Today's remaining doses — one entry per alarm still to come, listing the
 * compartments due then. A pure schedule lookup (is this alarm time still
 * ahead of now); it doesn't judge whether anything already fired was taken.
 */
export const upcomingDoses = (boxes = [], zone) => {
  const now = minutesNow(zone);
  const slots = new Map();

  boxes.forEach((box, index) => {
    if (!box.medicineName) return;
    const boxNum = index + 1;

    (box.alarms || [])
      .filter((code) => code > now)
      .forEach((code) => {
        if (!slots.has(code)) slots.set(code, []);
        slots.get(code).push({ boxNumber: boxNum, medicineName: box.medicineName });
      });
  });

  return [...slots.entries()].sort((a, b) => a[0] - b[0]).map(([code, doses]) => ({ code, label: alarmLabel(code), doses }));
};

/** Total doses scheduled today across all boxes, fired or not. */
export const dosesScheduledToday = (boxes = []) =>
  boxes.reduce((sum, box) => sum + (box.medicineName ? (box.alarms || []).filter((code) => code > 0).length : 0), 0);

/** Hours/minutes from now (in `zone`) until an alarm code fires. */
export const timeUntil = (code, zone) => {
  const delta = code - minutesNow(zone);
  return { inHours: Math.floor(delta / 60), inMinutes: delta % 60 };
};

/** Recent events grouped by day, newest first. */
export const groupEvents = (days = [], zone, limit = 3) =>
  [...days]
    .reverse()
    .filter((day) => day.notifications?.length)
    .slice(0, limit)
    .map((day) => {
      const date = parseDay(day.date, zone);
      const relative = date.toRelativeCalendar({ unit: "days" }) || "";
      return {
        date: day.date,
        heading: `${relative.charAt(0).toUpperCase()}${relative.slice(1)}, ${date.toFormat("d LLL")}`,
        events: day.notifications.map((n) => ({
          id: n.timestamp + n.code,
          time: clockLabel(n.timestamp, zone),
          message: n.message,
          notification: n,
        })),
      };
    });
